/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, Suspense, lazy, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { default as useSound } from "use-sound";
import PrivateRoute from "./components/private-route/PrivateRoute";
import Preloader from "./components/preloader";
import { callSetAvailable, callWSStartConnecting } from "./app/call/callSlice";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import {
  chatWSConnecting,
  chatWSDisconnecting,
  chatWSOffline,
  firstIncomingChatRoom,
  setChatConfiguration,
} from "./app/chat/chatSlice";
import chatRingtone from "../public/sound/chat-ringtone.mp3";
import LeadsEmpty from "./components/chat/EmptyMessage";
import MessageAgentEmpty from "./pages/chat-agent/components/EmptyMessage";
import MessageBarIndex from "./pages/chat/components/MessageBarIndex";
import { useHttpRequest } from "./hooks/useHttpRequest";
import {
  dashboardWSConnecting,
  dashboardWSDisconnecting,
} from "./app/dashboard/dashboardSlice";
import ResponsePage from "./pages/ticket/ResponsePage";

const ApplicationManagement = lazy(
  () => import("./pages/application-management/MainView")
);
const PartnerManagement = lazy(
  () => import("./pages/product-sponsor-management")
);
const WhatsappBroadcastTemplateMain = lazy(
  () => import("./pages/whatsapp-broadcast-template")
);
const WhatsappBroadcastTemplate = lazy(
  () =>
    import("./pages/whatsapp-broadcast-template/components/BroadcastTemplate")
);
const WebLinkConfigurationMain = lazy(
  () => import("./pages/web-link-configuration")
);
const CustomAUXConfiguration = lazy(
  () => import("./pages/custom-aux-configuration")
);
const WhatsappBroadcastConfiguration = lazy(
  () => import("./pages/whatsapp-broadcast-configuration")
);
const UserRole = lazy(() => import("./pages/user-role-management/ListPage"));
const AddUserRole = lazy(() => import("./pages/user-role-management/AddPage"));
const EditUserRole = lazy(
  () => import("./pages/user-role-management/EditPage")
);
const UserList = lazy(() => import("./pages/user-management/ListPage"));
const EditUserList = lazy(() => import("./pages/user-management/EditPage"));
const AddUserPage = lazy(() => import("./pages/user-management/AddPage"));
const ResetPassword = lazy(
  () => import("./pages/user-list/pages/ResetPassword")
);
const TeamManagement = lazy(
  () => import("./pages/user-list/pages/TeamManagement")
);

const Dashboard = lazy(() => import("./pages/dashboard"));
const DashboardInbound = lazy(() => import("./pages/dashboard-inbound"));
const Profile = lazy(() => import("./pages/profile/index"));
const ProfilePage = lazy(() => import("./pages/profile/components/profile"));
const ProfileEditPage = lazy(
  () => import("./pages/profile/components/profile-edit")
);
const NotificationPage = lazy(
  () => import("./pages/profile/components/notification")
);
const SettingsPage = lazy(() => import("./pages/profile/components/settings"));
const SecurityPage = lazy(() => import("./pages/profile/components/security"));
const ChatAgentPage = lazy(() => import("./pages/chat-agent/index"));
const ChatPage = lazy(() => import("./pages/chat/index"));
const ChatMonitor = lazy(() => import("./pages/chat-monitor"));
const MessageAgentContent = lazy(
  () => import("./pages/chat-agent/components/MessageBar")
);
const MessageContent = lazy(() => import("./pages/chat/components/MessageBar"));
const ChangePasswordPage = lazy(
  () => import("./pages/profile/components/security-changepassword")
);

const Signin = lazy(() => import("./pages/auth/index"));
const Signinbox = lazy(() => import("./pages/auth/SigninBox"));

const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ChangePassword = lazy(() => import("./pages/auth/ChangePassword"));
const Otp = lazy(() => import("./pages/auth/OTP"));
const SignOut = lazy(() => import("./pages/auth/SignOut"));
const Error500 = lazy(() => import("./pages/errors/error-500"));
const Error503 = lazy(() => import("./pages/errors/error-503"));
const Error505 = lazy(() => import("./pages/errors/error-505"));
const ErrorNotFound = lazy(() => import("./pages/errors/error-404"));
const ErrorForbidden = lazy(() => import("./pages/errors/error-403"));
const Leads = lazy(() => import("./pages/leads/index"));
const Recording = lazy(() => import("./pages/recording"));
const ExportRecording = lazy(() => import("./pages/export-recording"));
const ExportRecordingSales = lazy(
  () => import("./pages/export-recording/indexSales")
);
const AgentMonitoring = lazy(() => import("./pages/agent-monitoring"));
const RobocallMonitoring = lazy(() => import("./pages/robocall-monitoring"));
const CampaingMain = lazy(
  () => import("./pages/campaign-management/components/CampaignMain")
);
const AddCampaign = lazy(
  () => import("./pages/campaign-management/components/AddCampaign")
);
const SystemManagementEmpty = lazy(
  () => import("./pages/system-management/empty")
);

const LeadsContent = lazy(() => import("./pages/leads/components/LeadsBar"));

const ExtractApp = lazy(() => import("./pages/extract-application/index"));

const AgentProductionReport = lazy(
  () => import("./pages/report/agent_production-report")
);
const AgentProductionReportHierarchy = lazy(
  () => import("./pages/report/agent_production_hierarchy-report")
);
const DailyProductionReport = lazy(
  () => import("./pages/report/daily_production-report")
);
const CampaignProductivityReport = lazy(
  () => import("./pages/report/campaign_productivity-report")
);
const DCCalculationReport = lazy(
  () => import("./pages/report/dc_calculation_report")
);
const CallTrackingReport = lazy(
  () => import("./pages/report/call_tracking-report")
);
const CallMonitoringReport = lazy(
  () => import("./pages/report/call_monitoring-report")
);
const CalculationDCByAgentReport = lazy(
  () => import("./pages/report/calculation_dc_by_agentdc")
);
const DailyQAReport = lazy(() => import("./pages/report/daily_qa_report"));
const QCRateReport = lazy(() => import("./pages/report/qc_rate-report"));
const ServiceLevelReport = lazy(
  () => import("./pages/report/service_level-report")
);
const AHTByAgentReport = lazy(
  () => import("./pages/report/aht_by_agent-report")
);
const AHTByHourReport = lazy(() => import("./pages/report/aht_by_hour-report"));
const ProductivityLoginLogoutReport = lazy(
  () => import("./pages/report/productivity_login_logout-report")
);
const StatusCallReport = lazy(
  () => import("./pages/report/status_call-report")
);
const DetailConversationReport = lazy(
  () => import("./pages/report/detail_conversation-report")
);
const AUXReport = lazy(() => import("./pages/report/aux-report"));
const AccountExtendReport = lazy(
  () => import("./pages/report/telecollection/account_extend-report")
);
const CurrentAccountReport = lazy(
  () => import("./pages/report/telecollection/current_account-report")
);
const DailyToBankReport = lazy(
  () => import("./pages/report/telecollection/daily_to_bank-report")
);
const DiscountLunasReport = lazy(
  () => import("./pages/report/telecollection/discount_lunas-report")
);
const PTPToBankReport = lazy(
  () => import("./pages/report/telecollection/ptp_to_bank-report")
);
const WithdrawalReport = lazy(
  () => import("./pages/report/telecollection/withdrawal-report")
);
const RescheduleReport = lazy(
  () => import("./pages/report/telecollection/reschedule-report")
);
const PrivacyPolicy = lazy(() => import("./pages/privacy-policy"));

import {
  IncomingChatAgentRoom,
  chatAgentWSConnecting,
} from "./app/chatAgent/chatAgentSlice";
import { setAvailable } from "./app/userState/userStateSlice";
import { logout } from "./app/auth/authSlice";
import { IResponse } from "./hooks/useHttp";
import { IIncomingRoom } from "./app/chatAgent/types";
import {
  vCallSetAvailable,
  vCallWSStartConnecting,
} from "./app/videocall/videoSlice";
import { sendLogoutStatus } from "./app/auth/api";
import WidgetSample from "./pages/widget-sample";
import WidgetSampleMobile from "./pages/widget-sample-mobile";
import OTPLogin from "./pages/auth/OTPLogin";

const TemplateMain = lazy(
  () => import("./pages/upload-template/components/TemplateMain")
);
const UploadDataMain = lazy(
  () => import("./pages/upload-data/components/UploadDataMain")
);
const ExtractDataMain = lazy(
  () => import("./pages/extract-data/components/ExtractDataMain")
);
const ExtractTemplateMain = lazy(
  () => import("./pages/extract-data/components/ExtractTemplateMain")
);
const FormHeader = lazy(
  () => import("./pages/upload-template/components/FormHeader")
);
const UDFormHeader = lazy(
  () => import("./pages/upload-data/components/FormHeader")
);
const DataDetail = lazy(
  () => import("./pages/upload-data/components/DataDetail")
);
const BudgetMain = lazy(() => import("./pages/budget/index"));

const ScriptMain = lazy(
  () => import("./pages/product-script/components/ScriptMain")
);

const SurveyManagementIndexPage = lazy(
  () => import("./pages/survey-management/ListPage")
);
const SurveyManagementAddPage = lazy(
  () => import("./pages/survey-management/AddPage")
);
const SurveyManagementEditPage = lazy(
  () => import("./pages/survey-management/EditPage")
);

const QaManagementIndexPage = lazy(
  () => import("./pages/qa-management/ListPage")
);
const QaManagementAddPage = lazy(() => import("./pages/qa-management/AddPage"));
const QaManagementEditPage = lazy(
  () => import("./pages/qa-management/EditPage")
);

const InteractionManagementIndexPage = lazy(
  () => import("./pages/interaction-management/ListPage")
);
const InteractionManagementAddPage = lazy(
  () => import("./pages/interaction-management/AddPage")
);
const InteractionManagementEditPage = lazy(
  () => import("./pages/interaction-management/EditPage")
);

const ApplicationMonitoringIndexPage = lazy(
  () => import("./pages/application-monitoring/ListPage")
);

const ApplicationMonitoringDetailPage = lazy(
  () => import("./pages/application-monitoring/DetailPage")
);
const RecordingNonSalesQAScoreDetailPage = lazy(
  () => import("./pages/export-recording/DetailPage")
);

const PageManagementListPage = lazy(
  () => import("./pages/page-management/ListPage")
);

const PageManagementAddPage = lazy(
  () => import("./pages/page-management/FormPage")
);

const PageManagementEditPage = lazy(
  () => import("./pages/page-management/FormPage")
);

const MenuManagement = lazy(() => import("./pages/menu-management/ListPage"));

const MenuPageAccess = lazy(() => import("./pages/menu-page-access/ListPage"));

const MenuPageAccessAddPage = lazy(
  () => import("./pages/menu-page-access/FormPage")
);

const MenuPageAccessEditPage = lazy(
  () => import("./pages/menu-page-access/FormPage")
);

const UploadProductData = lazy(
  () => import("./pages/upload-product-data/ListPage")
);

const UploadProductDataAdd = lazy(
  () => import("./pages/upload-product-data/FormPage")
);

const ManualDial = lazy(
  () => import("./pages/manual-dialer/components/ManualDialerMain")
);

const TelcoProviderMain = lazy(() => import("./pages/telco-provider/index"));
const Approval = lazy(() => import("./pages/approval/index"));
const AssignmentConfiguration = lazy(
  () => import("./pages/assignment-configuration/index")
);
const Chat = lazy(() => import("./pages/chat/index"));

const SchedulerMain = lazy(() => import("./pages/scheduler/index"));

const IVRManagementListPage = lazy(
  () => import("./pages/ivr-management/ListPage")
);
const IVRManagementAddPage = lazy(
  () => import("./pages/ivr-management/AddPage")
);
const EmailBlast = lazy(() => import("./pages/email-blast"));
const EmailBlastAdd = lazy(() => import("./pages/email-blast/components/Add"));
const EmailSignature = lazy(() => import("./pages/email-signature-template"));
const CustomerInteraction = lazy(() => import("./pages/customer-interaction"));
const CustomerDetail = lazy(
  () => import("./pages/customer-interaction/components/DetailView")
);
const WhatsappBroadcast = lazy(() => import("./pages/whatsapp-broadcast"));
const AddBroadcastView = lazy(
  () => import("./pages/whatsapp-broadcast/components/AddBroadcastView")
);

const BotAssignment = lazy(() => import("./pages/bot-assignment"));
const BotAssignmentView = lazy(
  () => import("./pages/bot-assignment/components/BotAssignmentView")
);
const Csat = lazy(() => import("./pages/ticket/CSATPage"));

const TelevideoConfigPage = lazy(
  () => import("./pages/televideo-config/FormConfig")
);
const ServiceGroup = lazy(() => import("./pages/service-group"));
const DetailServiceGroupView = lazy(
  () => import("./pages/service-group/components/DetailView")
);
const ChannelIntegration = lazy(() => import("./pages/channel-integration"));
const AddChannelView = lazy(
  () => import("./pages/channel-integration/components/AddChannelView")
);

const EformLoanCustomer = lazy(
  () => import("./pages/eform-loan/EformLoanCustomer")
);
const CustomerUploadPurgingIndexPage = lazy(
  () => import("./pages/customer-upload-purging/ListPage")
);
const Wallboard = lazy(() => import("./pages/wallboard"));
const WallboardPerformance = lazy(
  () => import("./pages/wallboard-performance")
);

const WallboardTraffic = lazy(() => import("./pages/wallboard-traffic"));

const TenantManagementIndex = lazy(
  () => import("./pages/tenant-management/index")
);
const TicketMonitor = lazy(() => import("./pages/ticket-monitor"));

const Payment = lazy(() => import("./pages/payment/PaymentMain"));
const Outstanding = lazy(
  () => import("./pages/payment/components/Outstanding")
);
const Withdrawal = lazy(() => import("./pages/withdrawal/index"));

const VariableConfiguration = lazy(
  () => import("./pages/variable-configuration/ListPage")
);
const VariableConfigurationForm = lazy(
  () => import("./pages/variable-configuration/FormPage")
);
const QuickReply = lazy(() => import("./pages/quick-reply/ListPage"));
const QuickReplyForm = lazy(() => import("./pages/quick-reply/FormPage"));
const AccountDeletionPage = lazy(() => import("./pages/account-deletion"));
const FacebookeLogin = lazy(() => import("./pages/facebook-login"));

declare global {
  interface Window {
    getWindowLocation(): void;
    copyWindowLocation(): void;
    makeFullScreen(divId: any): void;

    conference: any;
  }
}

export const LayoutContext = React.createContext<any>(null);

const App: FC = () => {
  const chatAgentServiceUrl = process.env.REACT_APP_CHAT_AGENT_SERVICE;
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { vCallStatus } = useAppSelector((state) => state.vcall);
  const { incomingRoom, lastMessageRoomOwner, firstIncomingRoom } =
    useAppSelector((state) => state.chat);
  const leadsPath = "/leads";
  const messagesPath = "/messages";
  const systemManagementPath = "/system-management";
  const profilePath = "/profile";
  const chatPath = "/chat";
  const channelIntegrationPath = "channel-integration";
  const logoutProcess = async () => {
    dispatch(chatWSDisconnecting());
    dispatch(dashboardWSDisconnecting());
    dispatch(setAvailable(false));
    dispatch(logout());
    await sendLogoutStatus(user);
  };
  useIdleTimer({
    onIdle: logoutProcess,
    timeout: 1800000,
    throttle: 1000,
  });

  const { sendRequest } = useHttpRequest();
  const loadChatAgentRoom = async () => {
    const retrieveData = (res: IResponse | undefined) => {
      const data = res?.data;
      const chatRooms =
        data?.map((item) => {
          return {
            roomId: item.room_id,
            sessionId: item.session_id,
            fullName: item.room_name,
            userName: item.username,
            latestMessage: item.latest_message,
            latestMessageTime: item.latest_message_time,
            unreadMessageCount: item.unread_message_count,
            status: item.status,
            roleId: item.role_id,
            participant: item.participant,
          } as IIncomingRoom;
        }) ?? [];
      dispatch(IncomingChatAgentRoom(chatRooms));
    };
    const response = await sendRequest({
      baseUrl: chatAgentServiceUrl,
      url: `/Room/${user?.username}`,
      method: "get",
    });
    retrieveData(response);
  };

  const loadConfig = async () => {
    const chatServiceUrl = String(process.env.REACT_APP_CHAT_SERVICE);
    const result = await sendRequest({
      baseUrl: chatServiceUrl,
      url: `/configuration/`,
      method: "get",
      params: { tenantid: user?.tenantId },
    });
    if (result?.data) {
      const data = result?.data as any;
      dispatch(
        setChatConfiguration({
          allowAttachment: data.allow_attachment,
          maxAssignment: data.max_assignment ?? 0,
          resolveOnOutboundSaveInteraction:
            data.resolve_on_outbound_save_interaction ?? true,
        })
      );
    }
  };
  const requestBrowserNotification = async () => {
    if (!("Notification" in window)) {
      console.log("Browser does not support desktop notification");
    } else {
      await Notification.requestPermission();
    }
  };
  useEffect(() => {
    if (user?.level == 1) {
      const unreadCount = incomingRoom
        .map((a) => a.unreadMessageCount)
        .reduce((val, a) => Number(val) + Number(a), 0);
      document.title =
        unreadCount > 0
          ? `${unreadCount} New message - ARSI`
          : "Digital Contact Center - ARSI";
    }
  }, [incomingRoom, user]);

  const [play] = useSound(chatRingtone);

  useEffect(() => {
    if (firstIncomingRoom.length > 0 || lastMessageRoomOwner) play();
    if (firstIncomingRoom.length > 0) {
      new Notification(`New message from ${firstIncomingRoom[0].fullName}`, {
        body: firstIncomingRoom[0]?.latestMessage,
      });
      dispatch(firstIncomingChatRoom([]));
    }
  }, [lastMessageRoomOwner, firstIncomingRoom]);

  useEffect(() => {
    requestBrowserNotification().catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    if (user?.username)
      loadChatAgentRoom().catch((err) =>
        console.log("failed load chat agent room", err)
      );
  }, [user]);

  useEffect(() => {
    if (
      user?.deviceId != "" &&
      user?.deviceId !== undefined &&
      vCallStatus !== ""
    ) {
      dispatch(chatWSConnecting());
      dispatch(chatAgentWSConnecting());

      if (!(user?.status == "offline" && user?.deviceInfo != "disconnected")) {
        dispatch(vCallWSStartConnecting());
        dispatch(callWSStartConnecting());
      } else {
        dispatch(callSetAvailable(false));
        dispatch(vCallSetAvailable(false));
        dispatch(setAvailable(false));

        setTimeout(() => {
          dispatch(chatWSOffline());
        }, 2000);
      }
      dispatch(dashboardWSConnecting());
    }
    if (user) {
      loadConfig().catch((e) => console.log(e));
    }
  }, [user]);

  return (
    <Router>
      <Suspense fallback={<Preloader />}>
        <Routes>
          {/* Sample Widget Routes */}
          <Route path="/widget-sample" element={<WidgetSample />} />
          <Route
            path="/widget-sample-mobile"
            element={<WidgetSampleMobile />}
          />

          {/* Dashboard Routes */}
          <Route
            path="/"
            element={
              <PrivateRoute showSidebar={false}>
                <DashboardInbound />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute showSidebar={false}>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard-inbound"
            element={
              <PrivateRoute showSidebar={false}>
                <DashboardInbound />
              </PrivateRoute>
            }
          />

          <Route
            path="/wallboard"
            element={
              <PrivateRoute showSidebar={false}>
                <Wallboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/wallboard-traffic"
            element={
              <PrivateRoute showSidebar={false}>
                <WallboardTraffic />
              </PrivateRoute>
            }
          />

          <Route
            path="/wallboard-performance"
            element={
              <PrivateRoute showSidebar={false}>
                <WallboardPerformance />
              </PrivateRoute>
            }
          />

          <Route
            path="/ticket-monitor"
            element={
              <PrivateRoute showSidebar={false}>
                <TicketMonitor />
              </PrivateRoute>
            }
          />

          <Route
            path="/customer-interaction"
            element={
              <PrivateRoute showSidebar={false}>
                <CustomerInteraction />
              </PrivateRoute>
            }
          >
            <Route path=":custid" element={<CustomerDetail />} />
          </Route>
          {/* Pages */}

          <Route path="/signin" element={<Signin />}>
            <Route index element={<Signinbox />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="otp-login" element={<OTPLogin />} />
            <Route path="otp" element={<Otp />} />
            <Route path="change-password" element={<ChangePassword />} />
          </Route>
          <Route path="/signout" element={<SignOut />} />

          <Route
            path="/eform-loan/:customerId/:campaignId/:userId"
            element={<EformLoanCustomer />}
          />

          <Route
            path={leadsPath}
            element={
              <PrivateRoute showSidebar={false}>
                <Leads />
              </PrivateRoute>
            }
          >
            <Route index element={<LeadsEmpty />} />
            <Route path=":customerid" element={<LeadsContent />} />
          </Route>
          <Route
            path={profilePath}
            element={
              <PrivateRoute showSidebar={false}>
                <Profile />
              </PrivateRoute>
            }
          >
            <Route index element={<ProfilePage />} />
            <Route path="edit" element={<ProfileEditPage />} />
            <Route path="notification" element={<NotificationPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="change-password" element={<ChangePasswordPage />} />
          </Route>
          <Route path={systemManagementPath}>
            <Route
              index
              element={
                <PrivateRoute>
                  <SystemManagementEmpty />
                </PrivateRoute>
              }
            />
            <Route
              path="user-role"
              element={
                <PrivateRoute>
                  <UserRole />
                </PrivateRoute>
              }
            />
            <Route
              path="user-role/add"
              element={
                <PrivateRoute>
                  <AddUserRole />
                </PrivateRoute>
              }
            />
            <Route
              path="user-role/edit/:roleId"
              element={
                <PrivateRoute>
                  <EditUserRole />
                </PrivateRoute>
              }
            />

            <Route
              path="user-list"
              element={
                <PrivateRoute>
                  <UserList />
                </PrivateRoute>
              }
            />
            <Route
              path="withdrawal"
              element={
                <PrivateRoute>
                  <Withdrawal />
                </PrivateRoute>
              }
            />
            <Route
              path="user-list/add"
              element={
                <PrivateRoute>
                  <AddUserPage />
                </PrivateRoute>
              }
            />
            <Route
              path="user-list/:userid"
              element={
                <PrivateRoute>
                  <EditUserList />
                </PrivateRoute>
              }
            />
            <Route
              path="user-list/team-management"
              element={
                <PrivateRoute>
                  <TeamManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="user-list/resetpassword/:userid"
              element={
                <PrivateRoute>
                  <ResetPassword />
                </PrivateRoute>
              }
            />

            <Route
              path="page-management"
              element={
                <PrivateRoute>
                  <PageManagementListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="page-management/add"
              element={
                <PrivateRoute>
                  <PageManagementAddPage />
                </PrivateRoute>
              }
            />
            <Route
              path="page-management/:id"
              element={
                <PrivateRoute>
                  <PageManagementEditPage />
                </PrivateRoute>
              }
            />
            <Route
              path="tenant-management"
              element={
                <PrivateRoute>
                  <TenantManagementIndex />
                </PrivateRoute>
              }
            >
              <Route
                path=":TenantType"
                element={
                  <PrivateRoute>
                    <TenantManagementIndex />
                  </PrivateRoute>
                }
              />
              <Route
                path=":TenantType/:id"
                element={
                  <PrivateRoute>
                    <TenantManagementIndex />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route
              path="menu-management/"
              element={
                <PrivateRoute>
                  <MenuManagement />
                </PrivateRoute>
              }
            />

            <Route
              path="menu-page-access/"
              element={
                <PrivateRoute>
                  <MenuPageAccess />
                </PrivateRoute>
              }
            />

            <Route
              path="menu-page-access/add"
              element={
                <PrivateRoute>
                  <MenuPageAccessAddPage />
                </PrivateRoute>
              }
            />

            <Route
              path="menu-page-access/:id"
              element={
                <PrivateRoute>
                  <MenuPageAccessEditPage />
                </PrivateRoute>
              }
            />

            <Route
              path="recording"
              element={
                <PrivateRoute>
                  <Recording />
                </PrivateRoute>
              }
            />
            <Route
              path="manual-dial"
              element={
                <PrivateRoute>
                  <ManualDial />
                </PrivateRoute>
              }
            />
            <Route
              path="campaign-management"
              element={
                <PrivateRoute>
                  <CampaingMain />
                </PrivateRoute>
              }
            >
              <Route
                path=":campaignType"
                element={
                  <PrivateRoute>
                    <AddCampaign />
                  </PrivateRoute>
                }
              />
            </Route>
            <Route
              path="agent-monitoring"
              element={
                <PrivateRoute>
                  <AgentMonitoring />
                </PrivateRoute>
              }
            />
            <Route
              path="robocall-monitoring"
              element={
                <PrivateRoute>
                  <RobocallMonitoring />
                </PrivateRoute>
              }
            />

            <Route
              path="ticket-monitor"
              element={
                <PrivateRoute>
                  <TicketMonitor />
                </PrivateRoute>
              }
            />

            <Route
              path="export-recording"
              element={
                <PrivateRoute>
                  <ExportRecording />
                </PrivateRoute>
              }
            />
            <Route
              path="export-recording-nonsales"
              element={
                <PrivateRoute>
                  <ExportRecording />
                </PrivateRoute>
              }
            />
            <Route
              path="export-recording-nonsales/:id"
              element={
                <PrivateRoute showSidebar={false}>
                  <RecordingNonSalesQAScoreDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="export-recording-sales"
              element={
                <PrivateRoute>
                  <ExportRecordingSales />
                </PrivateRoute>
              }
            />

            <Route
              path="product-sponsor-management"
              element={
                <PrivateRoute>
                  <PartnerManagement />
                </PrivateRoute>
              }
            >
              <Route
                path=":FormType"
                element={
                  <PrivateRoute>
                    <PartnerManagement />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route
              path="whatsapp-broadcast-template"
              element={
                <PrivateRoute>
                  <WhatsappBroadcastTemplateMain />
                </PrivateRoute>
              }
            />
            <Route path="facebook-login" element={<PrivateRoute>
              <FacebookeLogin />
            </PrivateRoute>} />


            <Route
              path="whatsapp-broadcast-template/:templateId"
              element={
                <PrivateRoute>
                  <WhatsappBroadcastTemplate />
                </PrivateRoute>
              }
            />

            <Route
              path="web-link-configuration"
              element={
                <PrivateRoute>
                  <WebLinkConfigurationMain />
                </PrivateRoute>
              }
            />
            <Route
              path="custom-aux-configuration"
              element={
                <PrivateRoute>
                  <CustomAUXConfiguration />
                </PrivateRoute>
              }
            />

            <Route
              path="whatsapp-broadcast-configuration"
              element={
                <PrivateRoute>
                  <WhatsappBroadcastConfiguration />
                </PrivateRoute>
              }
            />

            <Route
              path="quick-reply"
              element={
                <PrivateRoute>
                  <QuickReply />
                </PrivateRoute>
              }
            />
            <Route
              path="quick-reply/add"
              element={
                <PrivateRoute>
                  <QuickReplyForm />
                </PrivateRoute>
              }
            />
            <Route
              path="quick-reply/:id"
              element={
                <PrivateRoute>
                  <QuickReplyForm />
                </PrivateRoute>
              }
            />

            <Route
              path="application-management"
              element={
                <PrivateRoute>
                  <ApplicationManagement />
                </PrivateRoute>
              }
            >
              <Route
                path=":formType"
                element={
                  <PrivateRoute>
                    <ApplicationManagement />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route
              path="export-recording"
              element={
                <PrivateRoute>
                  <ExportRecording />
                </PrivateRoute>
              }
            />
            <Route
              path="upload-template"
              element={
                <PrivateRoute>
                  <TemplateMain />
                </PrivateRoute>
              }
            >
              <Route
                path=":templateType"
                element={
                  <PrivateRoute>
                    <FormHeader />
                  </PrivateRoute>
                }
              ></Route>
            </Route>
            <Route
              path="upload-data"
              element={
                <PrivateRoute>
                  <UploadDataMain />
                </PrivateRoute>
              }
            >
              <Route
                path=":dataType"
                element={
                  <PrivateRoute>
                    <UDFormHeader />
                  </PrivateRoute>
                }
              ></Route>
              <Route
                path=":dataType"
                element={
                  <PrivateRoute>
                    <DataDetail />
                  </PrivateRoute>
                }
              ></Route>
            </Route>
            <Route
              path="upload-product-data"
              element={
                <PrivateRoute>
                  <UploadProductData />
                </PrivateRoute>
              }
            />
            <Route
              path="upload-product-data/add"
              element={
                <PrivateRoute>
                  <UploadProductDataAdd />
                </PrivateRoute>
              }
            />
            <Route
              path="qa-management"
              element={
                <PrivateRoute>
                  <QaManagementIndexPage />
                </PrivateRoute>
              }
            />
            <Route
              path="qa-management/add"
              element={
                <PrivateRoute>
                  <QaManagementAddPage />
                </PrivateRoute>
              }
            />
            <Route
              path="qa-management/:id"
              element={
                <PrivateRoute>
                  <QaManagementEditPage />
                </PrivateRoute>
              }
            />

            <Route
              path="interaction-management"
              element={
                <PrivateRoute>
                  <InteractionManagementIndexPage />
                </PrivateRoute>
              }
            />
            <Route
              path="interaction-management/add"
              element={
                <PrivateRoute>
                  <InteractionManagementAddPage />
                </PrivateRoute>
              }
            />
            <Route
              path="interaction-management/:id"
              element={
                <PrivateRoute>
                  <InteractionManagementEditPage />
                </PrivateRoute>
              }
            />
            <Route
              path="product-script"
              element={
                <PrivateRoute>
                  <ScriptMain />
                </PrivateRoute>
              }
            >
              <Route path=":scriptType"></Route>
            </Route>

            {/* Budget */}
            <Route
              path="budget"
              element={
                <PrivateRoute>
                  <BudgetMain />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="telco-provider"
              element={
                <PrivateRoute>
                  <TelcoProviderMain />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="approval"
              element={
                <PrivateRoute>
                  <Approval />
                </PrivateRoute>
              }
            />
            <Route
              path="assignment-configuration"
              element={
                <PrivateRoute>
                  <AssignmentConfiguration />
                </PrivateRoute>
              }
            />
            <Route
              path="extract-data"
              element={
                <PrivateRoute>
                  <ExtractDataMain />
                </PrivateRoute>
              }
            >
              <Route path=":formType"></Route>
            </Route>
            <Route
              path="extract-template"
              element={
                <PrivateRoute>
                  <ExtractTemplateMain />
                </PrivateRoute>
              }
            >
              <Route path=":formType"></Route>
            </Route>
            <Route
              path="agent_production-reports"
              element={
                <PrivateRoute>
                  <AgentProductionReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="extracted-application"
              element={
                <PrivateRoute>
                  <ExtractApp />
                </PrivateRoute>
              }
            />
            <Route
              path="agent_production-reports"
              element={
                <PrivateRoute>
                  <AgentProductionReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="apr_hierarchy-reports"
              element={
                <PrivateRoute>
                  <AgentProductionReportHierarchy />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="daily_production-reports"
              element={
                <PrivateRoute>
                  <DailyProductionReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="campaign_productivity-reports"
              element={
                <PrivateRoute>
                  <CampaignProductivityReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="daily_qa-reports"
              element={
                <PrivateRoute>
                  <DailyQAReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="qc_rate-reports"
              element={
                <PrivateRoute>
                  <QCRateReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="dc_calculation-reports"
              element={
                <PrivateRoute>
                  <DCCalculationReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="call_tracking-reports"
              element={
                <PrivateRoute>
                  <CallTrackingReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="call_monitoring-reports"
              element={
                <PrivateRoute>
                  <CallMonitoringReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="calculation_dc_agent-reports"
              element={
                <PrivateRoute>
                  <CalculationDCByAgentReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="status-call-report"
              element={
                <PrivateRoute>
                  <StatusCallReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="service_level-reports"
              element={
                <PrivateRoute>
                  <ServiceLevelReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="aht_by_agent-reports"
              element={
                <PrivateRoute>
                  <AHTByAgentReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="aht_by_Hour-reports"
              element={
                <PrivateRoute>
                  <AHTByHourReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="productivity_login-reports"
              element={
                <PrivateRoute>
                  <ProductivityLoginLogoutReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="detail_conversation-reports"
              element={
                <PrivateRoute>
                  <DetailConversationReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="aux-reports"
              element={
                <PrivateRoute>
                  <AUXReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="account_extend-reports"
              element={
                <PrivateRoute>
                  <AccountExtendReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="current_account-reports"
              element={
                <PrivateRoute>
                  <CurrentAccountReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="daily_to_bank-reports"
              element={
                <PrivateRoute>
                  <DailyToBankReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="discount_lunas-reports"
              element={
                <PrivateRoute>
                  <DiscountLunasReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="ptp_to_bank-reports"
              element={
                <PrivateRoute>
                  <PTPToBankReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="withdrawal-reports"
              element={
                <PrivateRoute>
                  <WithdrawalReport />
                </PrivateRoute>
              }
            ></Route>
            <Route
              path="reschedule-reports"
              element={
                <PrivateRoute>
                  <RescheduleReport />
                </PrivateRoute>
              }
            />
            <Route
              path="application"
              element={
                <PrivateRoute>
                  <ApplicationMonitoringIndexPage />
                </PrivateRoute>
              }
            />
            <Route
              path="application/:id"
              element={
                <PrivateRoute showSidebar={false}>
                  <ApplicationMonitoringDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="scheduler"
              element={
                <PrivateRoute>
                  {/* <UserRole /> */}
                  <SchedulerMain />
                </PrivateRoute>
              }
            >
              <Route path=":schedulerType"></Route>
            </Route>
            <Route
              path="ivr-management"
              element={
                <PrivateRoute>
                  <IVRManagementListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="ivr-management/add"
              element={
                <PrivateRoute>
                  <IVRManagementAddPage />
                </PrivateRoute>
              }
            />
            <Route
              path="ivr-management/:ivrSchemaId"
              element={
                <PrivateRoute>
                  <IVRManagementAddPage />
                </PrivateRoute>
              }
            />
            <Route
              path="customer-interaction"
              element={
                <PrivateRoute>
                  <CustomerInteraction />
                </PrivateRoute>
              }
            >
              <Route path=":custid" element={<CustomerDetail />} />
            </Route>

            <Route
              path="assignment-monitoring"
              element={
                <PrivateRoute>
                  <ChatMonitor />
                </PrivateRoute>
              }
            >
              <Route
                path=":assignmentType"
                element={
                  <PrivateRoute>
                    <ChatMonitor />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route
              path="email-blast"
              element={
                <PrivateRoute>
                  <EmailBlast />
                </PrivateRoute>
              }
            />
            <Route
              path="email-blast/add"
              element={
                <PrivateRoute>
                  <EmailBlastAdd />
                </PrivateRoute>
              }
            />
            <Route
              path="email-signature-template"
              element={
                <PrivateRoute>
                  <EmailSignature />
                </PrivateRoute>
              }
            />

            <Route
              path="whatsapp-broadcast-inbound"
              element={
                <PrivateRoute>
                  <WhatsappBroadcast />
                </PrivateRoute>
              }
            />

            <Route
              path="whatsapp-broadcast-inbound/:WABroadcastId"
              element={
                <PrivateRoute>
                  <AddBroadcastView />
                </PrivateRoute>
              }
            />

            <Route
              path="whatsapp-broadcast-outbound"
              element={
                <PrivateRoute>
                  <WhatsappBroadcast />
                </PrivateRoute>
              }
            />

            <Route
              path="whatsapp-broadcast-outbound/:WABroadcastId"
              element={
                <PrivateRoute>
                  <AddBroadcastView />
                </PrivateRoute>
              }
            />

            <Route
              path="bot-assignment"
              element={
                <PrivateRoute>
                  <BotAssignment />
                </PrivateRoute>
              }
            />

            <Route
              path="bot-assignment/:id"
              element={
                <PrivateRoute>
                  <BotAssignmentView />
                </PrivateRoute>
              }
            />

            <Route
              path="televideo-config"
              element={
                <PrivateRoute>
                  <TelevideoConfigPage />
                </PrivateRoute>
              }
            />

            <Route
              path="service-group"
              element={
                <PrivateRoute>
                  <ServiceGroup />
                </PrivateRoute>
              }
            />

            <Route
              path="service-group/:serviceGroupId"
              element={
                <PrivateRoute>
                  <DetailServiceGroupView />
                </PrivateRoute>
              }
            />

            <Route
              path="channel-integration"
              element={
                <PrivateRoute>
                  <ChannelIntegration />
                </PrivateRoute>
              }
            />

            <Route
              path="channel-integration/:channelId"
              element={
                <PrivateRoute>
                  <AddChannelView
                    mainRoute={`${systemManagementPath}${channelIntegrationPath}`}
                  />
                </PrivateRoute>
              }
            />

            <Route
              path="survey-management"
              element={
                <PrivateRoute>
                  <SurveyManagementIndexPage />
                </PrivateRoute>
              }
            />
            <Route
              path="survey-management/add"
              element={
                <PrivateRoute>
                  <SurveyManagementAddPage />
                </PrivateRoute>
              }
            />
            <Route
              path="survey-management/:id"
              element={
                <PrivateRoute>
                  <SurveyManagementEditPage />
                </PrivateRoute>
              }
            />

            {/* <Route path="channel-integration" > */}
            {/* Main Route Content */}
            {/* <Route index element={<PrivateRoute><ChannelIntegration /></PrivateRoute>} />
              <Route path=":channelId" element={<PrivateRoute><AddChannelView mainRoute={`${systemManagementPath}${channelIntegrationPath}`} /></PrivateRoute>} /> */}
            {/* Nested Route */}
            {/* <Route path="add" element={<PrivateRoute><AddChannelView /></PrivateRoute>} >
                                    
                                </Route> */}
            {/* </Route> */}

            <Route
              path="customer-upload-purging"
              element={
                <PrivateRoute>
                  <CustomerUploadPurgingIndexPage />
                </PrivateRoute>
              }
            />

            <Route
              path="payment"
              element={
                <PrivateRoute>
                  <Payment />
                </PrivateRoute>
              }
            />

            <Route
              path="payment/payment-outstanding"
              element={
                <PrivateRoute>
                  <Outstanding />
                </PrivateRoute>
              }
            />

            <Route
              path="variable-configuration"
              element={
                <PrivateRoute>
                  <VariableConfiguration />
                </PrivateRoute>
              }
            />
            <Route
              path="variable-configuration/add"
              element={
                <PrivateRoute>
                  <VariableConfigurationForm />
                </PrivateRoute>
              }
            />
            <Route
              path="variable-configuration/:id"
              element={
                <PrivateRoute>
                  <VariableConfigurationForm />
                </PrivateRoute>
              }
            />
          </Route>

          <Route
            path={chatPath}
            element={
              <PrivateRoute showSidebar={false}>
                <ChatAgentPage layoutType="PAGE" />
              </PrivateRoute>
            }
          >
            <Route index element={<MessageAgentEmpty />} />
            <Route path=":chatId" element={<MessageAgentContent />} />
          </Route>

          <Route
            path={messagesPath}
            element={
              <PrivateRoute showSidebar={false}>
                <ChatPage />
              </PrivateRoute>
            }
          >
            <Route index element={<MessageBarIndex />} />
            <Route path=":chatId" element={<MessageContent />} />
          </Route>
          <Route
            path="/ticket/:ticketNumber"
            element={<ResponsePage />}
          ></Route>
          <Route
            path="/customer_satisfaction/:csatid/:sessionid"
            element={<Csat />}
          ></Route>
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          {/* Error Pages */}
          <Route path="/error-403" element={<ErrorForbidden />} />
          <Route path="/error-500" element={<Error500 />} />
          <Route path="/error-503" element={<Error503 />} />
          <Route path="/error-505" element={<Error505 />} />

          {/* 404 Page Route */}
          <Route path="*" element={<ErrorNotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
